import React from 'react';
import {InlineDropdown} from '@spaced-out/ui-design-system/lib/components/InlineDropdown';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {MicDevice} from 'src/types/speech-to-text';
import {useElevation} from 'src/components/agentic-ui/elevation';
import css from './mic-accessory.css';


export interface MicAccessoryProps {
  devices: MicDevice[];
  /** '' = system default. */
  selectedDeviceId: string;
  onSelectDevice: (deviceId: string) => void;
  /** Notifies the parent when the device menu opens/closes, so it can keep the
   *  picker mounted while open (see MicControl). */
  onMenuOpenChange?: (open: boolean) => void;
  /** aria/label text — pass already-translated (this component does no i18n). */
  defaultMicLabel: string;
}

/**
 * The microphone device picker. Rendered only once permission is granted
 * (MicControl owns the denied/prompt states); visibility is controlled by the
 * parent, which reveals it on hover.
 */
export function MicAccessory({
  devices,
  selectedDeviceId,
  onSelectDevice,
  onMenuOpenChange,
  defaultMicLabel,
}: MicAccessoryProps) {
  // Lift the menu above the ask-ai panel's stacking context (see useElevation).
  const menuElevation = useElevation('menu');

  if (devices.length === 0) {
    return null;
  }

  // Synthetic "system default" entry first, then each concrete mic.
  const options: MenuOption[] = [
    {key: '', label: defaultMicLabel},
    ...devices.map((d) => ({key: d.deviceId, label: d.label || d.deviceId})),
  ];
  const buttonText =
    selectedDeviceId === ''
      ? defaultMicLabel
      : devices.find((d) => d.deviceId === selectedDeviceId)?.label ??
        defaultMicLabel;

  return (
    <InlineDropdown
      size="small"
      anchorPosition="top-start"
      classNames={{buttonWrapper: css.pickerButton}}
      elevation={menuElevation}
      onMenuOpen={() => onMenuOpenChange?.(true)}
      onMenuClose={() => onMenuOpenChange?.(false)}
      menu={{
        options,
        optionsVariant: 'radio',
        selectedKeys: [selectedDeviceId],
        // Size the popover to its options (not the narrow trigger) so long
        // device names aren't truncated.
        isFluid: false,
      }}
      onOptionSelect={(option: MenuOption) =>
        onSelectDevice(String(option.key))
      }
    >
      {buttonText}
    </InlineDropdown>
  );
}
